import React, { useState, useEffect, useRef } from "react";
import { Table, notification, Button, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import { SendOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const AccountStatisticsGrouped = () => {
  const { t } = useTranslation();

  const user = useRef({ ...JSON.parse(localStorage.getItem("user")) }).current;
  const role = user?.roles[user?.roles?.length - 1];
  const isAdmin = role?.name === "admin";

  const [statisticsData, setStatisticsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAccountStatistics = () => {
    setLoading(true);
    coreApi
      .get("/approval-payment/account-statistics")
      .then((res) => {
        const sortedData = res.data.sort((a, b) => {
          const nameA = a.account?.name || "";
          const nameB = b.account?.name || "";
          return nameA.localeCompare(nameB);
        });
        setStatisticsData(sortedData);
      })
      .catch((err) => {
        notification.error({
          message: t("finance.transactions.loadError"),
          description: err.message,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAccountStatistics();
  }, []);

  const columns = [
    {
      title: t("finance.accounts.accountName"),
      dataIndex: "account",
      key: "accountName",
      render: (account) => <Link to={"/accounts/" + account?.id}>{account.name}</Link>,
    },
    {
      title: t("finance.transactions.totalPaymentsForApproval"),
      dataIndex: "totalPendingAmount",
      key: "totalPending",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record.currency || "CZK",
      }).format(value),
    },
    {
      title: t("finance.transactions.totalPaymentsSent"),
      dataIndex: "totalPaidAmount",
      key: "totalPaid",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record.currency || "CZK",
      }).format(value),
    }
  ];

  if (isAdmin) {
    columns.push({
      title: t("finance.moneyReturn.sendPayment"),
      dataIndex: "btn",
      key: "btn",
      render: (_, record) => (
        <Button
          type={"primary"}
          icon={<SendOutlined />}
          onClick={() => confirmSendPayment(record)}
          loading={loading}
          disabled={record?.totalPendingCount === 0}
        >
          {t("finance.invoices.sendToRecipient")} ({record?.totalPendingCount})
        </Button>
      ),
    });
  }

  const confirmSendPayment = (record) => {
    Modal.confirm({
      title: t("finance.paymentsForApproval.confirmPaymentForAccount"),
      content:  record.account.name,
      onOk() {
        handleSendAccountApprovalPayment(record.account.id);
      },
    });
  };

  const groupErrorsByType = (errors) => {
    const grouped = {};
    errors.forEach(error => {
      const errorType = error.error;
      if (!grouped[errorType]) {
        grouped[errorType] = [];
      }
      grouped[errorType].push(error.contractor_name);
    });
    return grouped;
  };

  const formatErrorDetails = (errors) => {
    if (!errors || errors.length === 0) return '';
    
    const grouped = groupErrorsByType(errors);
    const errorMessages = Object.entries(grouped).map(([errorType, contractors]) => {
      return `${errorType}: ${contractors.join(', ')}`;
    });
    
    return errorMessages.join('\n');
  };

  const formatNotificationContent = (responseData) => {
    const { message, description, error_details } = responseData || {};
    
    let notificationMessage = message || t("finance.transactions.selectedRowsSavedSuccessfully");
    let notificationDescription = "";

    if (description && description.trim() !== "") {
      notificationDescription += description;
    }

    if (error_details?.user_actions && error_details.user_actions.length > 0) {
      if (notificationDescription) {
        notificationDescription += "\n\n";
      }
      notificationDescription += t("finance.transactions.recommendedActions", "Doporučené akce:") + "\n";
      notificationDescription += error_details.user_actions.map(action => `• ${action}`).join('\n');
    }
    
    return {
      message: notificationMessage,
      description: notificationDescription.trim() || undefined
    };
  };

  const handleSendAccountApprovalPayment = (accountId) => {
    setLoading(true);
    coreApi
      .post("/approval-payment/send-account-approval-payment/" + accountId)
      .then((res) => {
        const responseData = res.data || {};
        const { success, errors } = responseData;
        
        const { message, description } = formatNotificationContent(responseData);
        
        // Handle legacy errors format for backward compatibility
        let legacyErrorDetails = "";
        if (errors && errors.length > 0) {
          legacyErrorDetails = formatErrorDetails(errors);
        }
        
        const finalDescription = description || legacyErrorDetails || undefined;

        if (responseData.invalid_recipients?.length > 0) {
          // Nothing was uploaded, the description lists the EUR recipients to fix first
          notification.error({
            message,
            description: finalDescription,
            duration: 0,
            style: {
              whiteSpace: 'pre-line',
              maxWidth: '600px',
              width: '600px'
            }
          });
        } else if (success === false || (errors && errors.length > 0) || responseData.error_details?.has_bank_errors || responseData.error_details?.has_validation_errors) {
          // Show as warning for partial success or errors
          notification.warning({ 
            message,
            description: finalDescription,
            duration: 20,
            style: { 
              whiteSpace: 'pre-line',
              maxWidth: '600px',
              width: '600px'
            }
          });
        } else {
          // Show as success
          notification.success({
            message,
            description: finalDescription,
            duration: 20,
            style: { 
              whiteSpace: 'pre-line',
              maxWidth: '600px',
              width: '600px'
            }
          });
        }
        fetchAccountStatistics();
      })
      .catch((err) => {
        const errorData = err?.response?.data || {};
        
        const { message, description } = formatNotificationContent(errorData);
        
        let legacyErrorDetails = "";
        if (errorData.errors && errorData.errors.length > 0) {
          legacyErrorDetails = formatErrorDetails(errorData.errors);
        }
        
        const finalDescription = description || legacyErrorDetails || errorData.error || undefined;
        
        notification.error({
          message: message || err?.response?.data?.message || t("finance.transactions.sendError", "Chyba při odesílání plateb"),
          description: finalDescription,
          duration: 20, // 20 seconds as requested
          style: { 
            whiteSpace: 'pre-line',
            maxWidth: '600px',
            width: '600px'
          }
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Table
      columns={columns}
      dataSource={statisticsData}
      loading={loading}
      rowKey={(record) => record.id || Math.random()}
      pagination={false}
      size="small"
    />
  );
};

export default AccountStatisticsGrouped;
